'use client'

import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { Camera, ChevronLeft, ChevronRight, Play, Users, LogIn } from 'lucide-react'
import dynamic from 'next/dynamic'

const HLSPlayer = dynamic(() => import('@/components/HLSPlayer'), {
  ssr: false,
  loading: () => (
    <div className="w-full aspect-video bg-gray-200 rounded-lg flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )
})

interface SiteSettings {
  siteName: string
  siteDescription: string
  logoUrl: string | null
  navbarBgColor: string
  navbarTextColor: string
  navbarSubtext: string
  heroTitle: string
  heroBadgeText: string
  heroBadgeColor: string
  heroTextColor: string
  pageBgColor: string
  cardBgColor: string
  cardTextColor: string
  cardBorderRadius: string
  buttonBgColor: string
  buttonTextColor: string
  buttonText: string
  sectionTitle: string
  sectionTitleColor: string
  footerText: string
  footerBgColor: string
  footerTextColor: string
  showFooter: boolean
  showLoginButton: boolean
  showViewerCount: boolean
  carouselEnabled: boolean
  carouselInterval: number
  customCss: string | null
}

interface CameraData {
  id: string
  name: string
  description: string | null
  isActive: boolean
  hasStream: boolean
  streamUrl: string
  streamQuality: string
  streamDelay: number
  group: { id: string; name: string; color: string } | null
  viewerCount: number
}

const defaultSettings: SiteSettings = {
  siteName: 'Canlı Kamera İzleme Sistemi',
  siteDescription: 'Canlı kamera yayınlarını izleyin',
  logoUrl: null,
  navbarBgColor: '#1e3a5f',
  navbarTextColor: '#ffffff',
  navbarSubtext: 'Canlı Yayın Ağı',
  heroTitle: 'Canlı Kamera İzleme Sistemi',
  heroBadgeText: 'Canlı Yayın',
  heroBadgeColor: '#dc2626',
  heroTextColor: '#1e293b',
  pageBgColor: '#c5d5cb',
  cardBgColor: '#ffffff',
  cardTextColor: '#1e293b',
  cardBorderRadius: '12',
  buttonBgColor: '#1e3a5f',
  buttonTextColor: '#ffffff',
  buttonText: 'İzle',
  sectionTitle: 'Canlı Yayın Kameraları',
  sectionTitleColor: '#1e293b',
  footerText: '',
  footerBgColor: '#1e3a5f',
  footerTextColor: '#ffffff',
  showFooter: false,
  showLoginButton: true,
  showViewerCount: false,
  carouselEnabled: true,
  carouselInterval: 5,
  customCss: null
}

export default function Home() {
  const router = useRouter()
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings)
  const [cameras, setCameras] = useState<CameraData[]>([])
  const [loading, setLoading] = useState(true)
  const [carouselIndex, setCarouselIndex] = useState(0)
  const [selectedCamera, setSelectedCamera] = useState<CameraData | null>(null)
  const [hlsUrl, setHlsUrl] = useState<string>('')
  const [streamStatus, setStreamStatus] = useState<'idle' | 'starting' | 'active' | 'error'>('idle')
  const carouselTimerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    Promise.all([
      fetch('/api/settings').then(r => r.ok ? r.json() : defaultSettings),
      fetch('/api/public-cameras').then(r => r.ok ? r.json() : [])
    ]).then(([settingsData, camerasData]) => {
      setSettings({ ...defaultSettings, ...settingsData })
      setCameras(camerasData)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  // Carousel auto-play
  useEffect(() => {
    if (!settings.carouselEnabled || cameras.length <= 1) return
    carouselTimerRef.current = setInterval(() => {
      setCarouselIndex(prev => (prev + 1) % cameras.length)
    }, settings.carouselInterval * 1000)
    return () => {
      if (carouselTimerRef.current) clearInterval(carouselTimerRef.current)
    }
  }, [settings.carouselEnabled, settings.carouselInterval, cameras.length])

  const prevSlide = () => {
    setCarouselIndex(prev => prev === 0 ? cameras.length - 1 : prev - 1)
    resetCarouselTimer()
  }

  const nextSlide = () => {
    setCarouselIndex(prev => (prev + 1) % cameras.length)
    resetCarouselTimer()
  }

  const resetCarouselTimer = () => {
    if (carouselTimerRef.current) clearInterval(carouselTimerRef.current)
    if (settings.carouselEnabled && cameras.length > 1) {
      carouselTimerRef.current = setInterval(() => {
        setCarouselIndex(prev => (prev + 1) % cameras.length)
      }, settings.carouselInterval * 1000)
    }
  }

  const handleWatch = async (camera: CameraData) => {
    setSelectedCamera(camera)
    setStreamStatus('starting')
    setHlsUrl('')

    try {
      const response = await fetch('/api/streaming', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cameraId: camera.id,
          rtspUrl: camera.streamUrl,
          quality: camera.streamQuality || '1080p',
          delay: camera.streamDelay || 30
        })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.hlsUrl) {
          setHlsUrl(data.hlsUrl)
          setStreamStatus('active')
        } else {
          setStreamStatus('error')
        }
      } else {
        setStreamStatus('error')
      }
    } catch {
      setStreamStatus('error')
    }
  }

  const closePlayer = () => {
    setSelectedCamera(null)
    setHlsUrl('')
    setStreamStatus('idle')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: settings.pageBgColor }}>
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  const featuredCamera = cameras[carouselIndex] || null

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: settings.pageBgColor }}>
      {/* Custom CSS */}
      {settings.customCss && <style dangerouslySetInnerHTML={{ __html: settings.customCss }} />}

      {/* Navbar */}
      <nav className="navbar shadow-lg sticky top-0 z-50" style={{ backgroundColor: settings.navbarBgColor }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {settings.logoUrl ? (
              <img src={settings.logoUrl} alt="Logo" className="w-10 h-10 object-contain rounded-lg" />
            ) : (
              <div className="bg-white/20 p-2 rounded-lg">
                <Camera className="w-6 h-6" style={{ color: settings.navbarTextColor }} />
              </div>
            )}
            <div>
              <h1 className="font-bold text-lg leading-tight" style={{ color: settings.navbarTextColor }}>
                {settings.siteName}
              </h1>
            </div>
            <span className="text-sm opacity-70 ml-2 hidden sm:inline" style={{ color: settings.navbarTextColor }}>
              • {settings.navbarSubtext}
            </span>
          </div>

          {settings.showLoginButton && (
            <button
              onClick={() => router.push('/login')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-90"
              style={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: settings.navbarTextColor
              }}
            >
              <LogIn className="w-4 h-4" />
              <span className="hidden sm:inline">Giriş</span>
            </button>
          )}
        </div>
        {/* Gradient bottom line */}
        <div className="h-1 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-800" />
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
            {/* Left - Title */}
            <div className="lg:w-1/2 space-y-4">
              <span
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: settings.heroBadgeColor }}
              >
                <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                {settings.heroBadgeText}
              </span>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight"
                style={{ color: settings.heroTextColor }}
              >
                {settings.heroTitle}
              </h2>
              {settings.siteDescription && (
                <p className="text-base sm:text-lg opacity-70" style={{ color: settings.heroTextColor }}>
                  {settings.siteDescription}
                </p>
              )}
            </div>

            {/* Right - Carousel */}
            {settings.carouselEnabled && cameras.length > 0 && (
              <div className="lg:w-1/2 w-full">
                <div className="relative">
                  {/* Camera preview carousel */}
                  <div
                    className="relative overflow-hidden shadow-2xl"
                    style={{ borderRadius: '16px' }}
                  >
                    <div className="aspect-video bg-gradient-to-br from-gray-300 to-gray-400 relative">
                      {/* Snapshot placeholder */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <Camera className="w-16 h-16 text-white/50 mx-auto mb-2" />
                          <p className="text-white/70 text-lg font-medium">{featuredCamera?.name}</p>
                        </div>
                      </div>

                      {/* Camera name overlay */}
                      <div className="absolute bottom-3 left-3">
                        <div className="flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-lg">
                          <span className="w-2.5 h-2.5 bg-green-500 rounded-full" />
                          <span className="text-sm font-medium text-gray-800">{featuredCamera?.name}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Carousel Controls */}
                  {cameras.length > 1 && (
                    <>
                      <button
                        onClick={prevSlide}
                        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
                      >
                        <ChevronLeft className="w-5 h-5 text-gray-700" />
                      </button>
                      <button
                        onClick={nextSlide}
                        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
                      >
                        <ChevronRight className="w-5 h-5 text-gray-700" />
                      </button>
                    </>
                  )}

                  {/* Carousel dots */}
                  {cameras.length > 1 && (
                    <div className="flex justify-center gap-2 mt-4">
                      {cameras.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => { setCarouselIndex(idx); resetCarouselTimer() }}
                          className={`w-2.5 h-2.5 rounded-full transition-all ${
                            idx === carouselIndex ? 'bg-blue-500 w-6' : 'bg-gray-400/50'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Camera Cards Section */}
        {cameras.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-12">
            <h3
              className="text-2xl sm:text-3xl font-bold mb-8"
              style={{ color: settings.sectionTitleColor }}
            >
              {settings.sectionTitle}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {cameras.map(camera => (
                <div
                  key={camera.id}
                  className="camera-card group overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  style={{
                    backgroundColor: settings.cardBgColor,
                    borderRadius: `${settings.cardBorderRadius}px`
                  }}
                >
                  {/* Camera thumbnail */}
                  <div className="relative aspect-video bg-gradient-to-br from-gray-200 to-gray-300 overflow-hidden">
                    {/* Status indicator */}
                    <div className="absolute top-3 left-3 z-10">
                      <span className={`w-3 h-3 rounded-full inline-block ${camera.isActive ? 'bg-green-500 shadow-lg shadow-green-500/50' : 'bg-red-500'}`} />
                    </div>

                    {/* Camera icon placeholder */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Camera className="w-12 h-12 text-gray-400/50 group-hover:scale-110 transition-transform" />
                    </div>

                    {/* Viewer count badge */}
                    {settings.showViewerCount && camera.viewerCount > 0 && (
                      <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {camera.viewerCount}
                      </div>
                    )}
                  </div>

                  {/* Card Content */}
                  <div className="p-4">
                    <h4 className="font-bold text-base" style={{ color: settings.cardTextColor }}>
                      {camera.name}
                    </h4>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-xs opacity-50" style={{ color: settings.cardTextColor }}>
                        {camera.isActive ? '⏱ 7/24 Yayın' : '⏸ Çevrimdışı'}
                      </span>
                    </div>

                    {camera.group && (
                      <div className="mt-2">
                        <span
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor: (camera.group.color || '#3B82F6') + '20',
                            color: camera.group.color || '#3B82F6'
                          }}
                        >
                          {camera.group.name}
                        </span>
                      </div>
                    )}

                    <button
                      onClick={() => handleWatch(camera)}
                      className="mt-3 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-90 hover:shadow-md"
                      style={{
                        backgroundColor: settings.buttonBgColor,
                        color: settings.buttonTextColor,
                        border: `1px solid ${settings.buttonBgColor}`
                      }}
                    >
                      <Play className="w-3.5 h-3.5" />
                      {settings.buttonText}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No cameras */}
        {cameras.length === 0 && !loading && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 text-center">
            <Camera className="w-20 h-20 mx-auto mb-4 opacity-30" style={{ color: settings.heroTextColor }} />
            <h3 className="text-xl font-medium opacity-50" style={{ color: settings.heroTextColor }}>
              Henüz aktif kamera yok
            </h3>
          </div>
        )}
      </main>

      {/* Footer */}
      {settings.showFooter && settings.footerText && (
        <footer className="mt-auto" style={{ backgroundColor: settings.footerBgColor }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 text-center">
            <p className="text-sm" style={{ color: settings.footerTextColor }}>{settings.footerText}</p>
          </div>
        </footer>
      )}

      {/* Video Player Modal */}
      {selectedCamera && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <h3 className="font-bold text-lg text-gray-900">{selectedCamera.name}</h3>
              </div>
              <button
                onClick={closePlayer}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
              >
                ✕
              </button>
            </div>

            {/* Video */}
            <div className="aspect-video bg-black">
              {streamStatus === 'starting' && (
                <div className="w-full h-full flex flex-col items-center justify-center text-white gap-3">
                  <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm opacity-70">Yayın başlatılıyor...</p>
                </div>
              )}
              {streamStatus === 'error' && (
                <div className="w-full h-full flex flex-col items-center justify-center text-white gap-3">
                  <Camera className="w-12 h-12 opacity-50" />
                  <p className="text-sm opacity-70">Yayın şu an kullanılamıyor</p>
                  <button
                    onClick={() => handleWatch(selectedCamera)}
                    className="mt-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm transition-colors"
                  >
                    Tekrar dene
                  </button>
                </div>
              )}
              {streamStatus === 'active' && hlsUrl && (
                <HLSPlayer
                  src={hlsUrl}
                  autoplay={true}
                  muted={true}
                  className="w-full h-full"
                />
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                {selectedCamera.group && (
                  <span className="px-2 py-0.5 rounded-full text-xs" style={{
                    backgroundColor: (selectedCamera.group.color || '#3B82F6') + '20',
                    color: selectedCamera.group.color || '#3B82F6'
                  }}>
                    {selectedCamera.group.name}
                  </span>
                )}
                {settings.showViewerCount && selectedCamera.viewerCount > 0 && (
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {selectedCamera.viewerCount} izleyici
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-400">Canlı Yayın</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

